import React, { useState } from 'react';
import axios from 'axios';
import { Bell, Check, Loader2, AlertCircle, Sparkles } from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EarlyBirdNotifySection = () => {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || status === 'submitting') return;

    setStatus('submitting');
    setErrorMsg('');

    try {
      await axios.post(`${API}/newsletter/subscribe`, { email: email.trim() });
      setStatus('success');
      setEmail('');
      if (typeof window !== 'undefined' && window.__atfTrackLead) {
        window.__atfTrackLead('newsletter_signup', { form_id: 'early-bird' });
      }
    } catch (err) {
      setStatus('error');
      const detail = err?.response?.data?.detail;
      setErrorMsg(
        typeof detail === 'string'
          ? detail
          : 'Please enter a valid email address.'
      );
    }
  };

  return (
    <section
      id="early-bird"
      className="py-16 sm:py-20 relative overflow-hidden"
      data-testid="early-bird-section"
    >
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-pink-500/20 via-orange-500/15 to-yellow-500/10" />
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/40 to-black/80" />

      {/* Decorative blobs */}
      <div className="absolute top-1/2 -left-24 w-80 h-80 bg-pink-500/20 rounded-full blur-3xl" />
      <div className="absolute top-1/2 -right-24 w-80 h-80 bg-orange-500/20 rounded-full blur-3xl" />

      <div className="relative z-10 max-w-3xl mx-auto px-4 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-md border border-pink-500/40 mb-5">
          <Sparkles className="w-4 h-4 text-yellow-300" />
          <span className="text-white text-sm font-medium tracking-wider uppercase">Early Bird Alert</span>
        </div>

        <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white mb-4 leading-tight">
          Be first in line for Early Bird tickets
        </h2>
        <p className="text-white/85 text-base sm:text-lg max-w-xl mx-auto mb-8">
          Drop your email and we&apos;ll alert you the moment Early Bird pricing unlocks — plus exclusive brand announcements, lineup drops, and presale codes.
        </p>

        {status === 'success' ? (
          <div
            className="inline-flex flex-col items-center gap-3 bg-white/10 backdrop-blur-md border border-green-400/40 rounded-2xl px-6 py-6 max-w-md mx-auto"
            data-testid="early-bird-success"
          >
            <div className="w-14 h-14 rounded-full bg-green-400/20 flex items-center justify-center">
              <Check className="w-7 h-7 text-green-300" />
            </div>
            <div>
              <p className="text-white font-bold text-lg">You&apos;re on the list! 🌺</p>
              <p className="text-white/75 text-sm mt-1">
                We&apos;ll ping you the moment Early Bird tickets drop. In the meantime, follow <a href="https://www.instagram.com/alohatequilafest" target="_blank" rel="noopener noreferrer" className="text-pink-300 underline hover:text-pink-200">@alohatequilafest</a> for lineup reveals.
              </p>
            </div>
            <button
              onClick={() => setStatus('idle')}
              data-testid="early-bird-add-another"
              className="text-white/60 hover:text-white text-sm underline mt-2"
            >
              Add another email
            </button>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto"
            data-testid="early-bird-form"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              disabled={status === 'submitting'}
              data-testid="early-bird-email-input"
              className="flex-1 bg-white/10 backdrop-blur-md border border-white/30 rounded-full px-6 py-4 text-white placeholder:text-white/50 focus:outline-none focus:border-pink-400 focus:bg-white/15 transition-all disabled:opacity-60"
            />
            <Button
              type="submit"
              disabled={status === 'submitting' || !email}
              data-testid="early-bird-submit-button"
              className="bg-gradient-to-r from-pink-500 to-orange-500 hover:from-pink-600 hover:to-orange-600 text-white font-bold rounded-full px-8 py-4 h-auto transition-all duration-300 hover:scale-105 disabled:opacity-60 disabled:hover:scale-100 inline-flex items-center justify-center gap-2"
            >
              {status === 'submitting' ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Joining…
                </>
              ) : (
                <>
                  <Bell className="w-5 h-5" />
                  Notify Me
                </>
              )}
            </Button>
          </form>
        )}

        {status === 'error' && (
          <div
            className="mt-4 inline-flex items-center gap-2 bg-red-500/15 border border-red-400/40 text-red-200 text-sm rounded-full px-4 py-2"
            data-testid="early-bird-error"
          >
            <AlertCircle className="w-4 h-4" />
            {errorMsg}
          </div>
        )}

        <p className="text-white/50 text-xs mt-5">
          No spam. Unsubscribe anytime. 21+ event.
        </p>
      </div>
    </section>
  );
};

export default EarlyBirdNotifySection;
